/** @format */

// App.tsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import Header from "./components/Header";
import Home from "./components/Home";
import AboutUs from "./components/AboutUs";
import PilotTest from "./components/PilotTest";
import ContactPage from "./components/Contact";
import Footer from "./components/Footer";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import Intro from "./components/Intro";
import {
  trackPageView,
  initGA,
  trackEvent,
  trackError,
} from "./utils/analytics";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1a1a1a",
    },
    secondary: {
      main: "#ffffff",
    },
  },
  typography: {
    fontFamily: "Poppins, Arial, sans-serif",
  },
});

// A component to track route changes
const RouteListener: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Get page template type based on the pathname
    const getPageTemplate = (pathname: string): string => {
      if (pathname === "/") return "homepage";
      if (pathname === "/about") return "about";
      if (pathname === "/join") return "pilot-test";
      if (pathname === "/contact") return "contact";
      if (pathname === "/intro-7min") return "intro-video";
      if (pathname.includes("privacy-policy")) return "legal";
      if (pathname.includes("terms-of-service")) return "legal";
      return "standard";
    };

    // Track the page view with enhanced information
    const pageTitle = getPageTitle(location.pathname);
    const pageTemplate = getPageTemplate(location.pathname);

    trackPageView(location.pathname, pageTitle, pageTemplate);

    // Track the navigation as an event too
    trackEvent("navigation", {
      from_path: document.referrer,
      to_path: location.pathname,
    });
  }, [location]);

  // Helper to get a descriptive page title
  const getPageTitle = (pathname: string): string => {
    switch (pathname) {
      case "/":
        return "Home | eon.health";
      case "/about":
        return "About Us | eon.health";
      case "/join":
        return "Join Pilot Test | eon.health";
      case "/contact":
        return "Contact Us | eon.health";
      case "/intro-7min":
        return "Introduction Video | eon.health";
      case "/privacy-policy":
        return "Privacy Policy | eon.health";
      case "/terms-of-service":
        return "Terms of Service | eon.health";
      default:
        return document.title;
    }
  };

  return null;
};

const AppContent: React.FC = () => {
  return (
    <>
      <Header />
      <RouteListener />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/join" element={<PilotTest />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/intro-7min" element={<Intro />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
      </Routes>
      <Footer />
    </>
  );
};

const App: React.FC = () => {
  // Initialize Google Analytics when the app loads
  useEffect(() => {
    // Initialize Google Analytics with enhanced configuration
    initGA();

    // Track errors globally
    window.addEventListener("error", (event) => {
      const { message, filename, lineno, colno, error } = event;
      trackError(
        "javascript_error",
        message || "Unknown error",
        `${filename}:${lineno}:${colno}`
      );
    });

    // Track browser performance metrics
    if ("performance" in window && "getEntriesByType" in window.performance) {
      window.addEventListener("load", () => {
        setTimeout(() => {
          const perfEntries = window.performance.getEntriesByType("navigation");
          if (perfEntries.length > 0) {
            const navEntry = perfEntries[0] as PerformanceNavigationTiming;
            trackEvent("performance_metrics", {
              dns_time: Math.round(
                navEntry.domainLookupEnd - navEntry.domainLookupStart
              ),
              connection_time: Math.round(
                navEntry.connectEnd - navEntry.connectStart
              ),
              ttfb: Math.round(navEntry.responseStart - navEntry.requestStart),
              dom_load_time: Math.round(
                navEntry.domContentLoadedEventEnd - navEntry.startTime
              ),
              page_load_time: Math.round(
                navEntry.loadEventEnd - navEntry.startTime
              ),
            });
          }
        }, 0);
      });
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
};

export default App;
